import { useState, useEffect, useContext, createContext } from 'react'
import type { ReactNode } from 'react'
import type { User } from 'firebase/auth'
import { AuthService } from '../../services/firebase/auth'
import { UserService } from '../../services/api/userService'
import { setSentryUser as setSentryUserAsync, clearSentryUser as clearSentryUserAsync } from '../../services/monitoring/sentry'
import { setAuthToken, clearAuthToken, onSessionExpired, onAccountSuspended, onRegistrationClosed } from '../../services/api/authTokenManager'
import type { UserProfile } from '../../types/user'
import { logAuth, logger } from '../../utils/logger'
import { getStoredReferralCode, clearStoredReferralCode } from '../useReferralCapture'
import { ReferralService } from '../../services/referral/referralService'
import { trackEvent } from '../../utils/ga4'

const SIGNUP_PENDING_KEY = 'tt_signup_pending'
const SIGNUP_PENDING_MAX_AGE_MS = 10 * 60 * 1000 // 10 min
const NEW_PROFILE_WINDOW_MS = 60 * 1000 // profile created within last 60s = fresh signup

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  loading: boolean
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  getUserInfo: () => unknown
  refreshUserProfile: () => Promise<UserProfile | null>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)

  // Initialize auth state
  useEffect(() => {
    let mounted = true;
    
    // Check for redirect result first (for Google sign-in in development)
    const checkRedirectResult = async () => {
      try {
        const result = await AuthService.handleGoogleRedirectResult()
        if (result && mounted) {
          // Redirect sign-in successful
          setUser(result.user)
        }
      } catch (error) {
        // Redirect result error
      }
    }
    
    void checkRedirectResult()
    
    const unsubscribe = AuthService.onAuthStateChanged((user) => {
      if (!mounted) {
        return;
      }

      setUser(user)

      if (user) {
        // Set user for monitoring
        setSentryUserAsync({
          id: user.uid,
          ...(user.email && { email: user.email }),
          ...(user.displayName && { username: user.displayName })
        })

        // Get and store auth token for API requests
        const updateAuthToken = async () => {
          try {
            const token = await user.getIdToken(true);
            setAuthToken(token);
            logAuth('Token stored');
          } catch (error) {
            logger.error('Failed to get auth token', undefined, error instanceof Error ? error : undefined);
            setAuthToken(null);
          }
        }
        void updateAuthToken();

        // User registration is handled by UserService.getOrCreateUser below
        // No need for separate admin system registration during user auth

        // Create/fetch user profile using API service
        const loadUserProfile = async () => {
          try {
            const profile = await UserService.getOrCreateUser(user.uid, {
              ...(user.email && { email: user.email }),
              ...(user.displayName && { displayName: user.displayName }),
              ...(user.photoURL && { photoURL: user.photoURL })
            })
            setUserProfile(profile)

            // Fire canonical sign_up event only after backend profile confirmed.
            // Two triggers (either one fires event, once per session):
            //   1. SIGNUP_PENDING_KEY flag set by RegisterPage button click,
            //      still fresh (< 10 min old). Primary path.
            //   2. Profile createdAt within last 60s. Safety net for mobile
            //      OAuth redirect flow where localStorage flag may be lost
            //      (Safari in-app browsers, cross-tab redirects).
            // Returning users without flag AND with old profile: NO event.
            let signupFlagFresh = false
            try {
              const pendingStr = localStorage.getItem(SIGNUP_PENDING_KEY)
              if (pendingStr) {
                const age = Date.now() - Number(pendingStr)
                if (age >= 0 && age < SIGNUP_PENDING_MAX_AGE_MS) signupFlagFresh = true
                localStorage.removeItem(SIGNUP_PENDING_KEY) // always clear
              }
            } catch { /* ignore */ }

            let profileIsFresh = false
            if (profile?.createdAt) {
              const createdMs = new Date(profile.createdAt).getTime()
              if (!Number.isNaN(createdMs) && Date.now() - createdMs < NEW_PROFILE_WINDOW_MS) {
                profileIsFresh = true
              }
            }

            if (signupFlagFresh || profileIsFresh) {
              trackEvent('sign_up', { method: 'google' })
            }

            // Attribute referral signup if a ref code was captured from URL.
            // Guard: only attempt once per page load (onAuthStateChanged fires on
            // token refresh / tab focus too, not just login).
            const refCode = getStoredReferralCode();
            if (refCode && !sessionStorage.getItem('tt_ref_attempted')) {
              sessionStorage.setItem('tt_ref_attempted', '1');
              ReferralService.getInstance()
                .attributeSignup({ code: refCode })
                .then((res) => {
                  if (res.accepted) {
                    console.log('[referral] signup attributed:', refCode);
                    clearStoredReferralCode();
                  } else {
                    if (res.reason !== 'no_active_attribution') {
                      clearStoredReferralCode();
                    }
                    console.log('[referral] attribution skipped:', res.reason);
                  }
                })
                .catch((err) => console.warn('[referral] attribution error:', err));
            }
          } catch (error) {
            // Error loading user profile - this will be handled by the service
            setUserProfile(null)
            // Clear signup flag so a subsequent successful login doesn't fire
            // sign_up against the stale intent from this failed signup attempt.
            try { localStorage.removeItem(SIGNUP_PENDING_KEY) } catch { /* ignore */ }
          }
        }

        void loadUserProfile()

        // User authenticated
      } else {
        // Clear user data on sign out
        clearSentryUserAsync()
        clearAuthToken()
        setUserProfile(null)
        // User signed out
      }
      
      // Add a small delay to ensure state is properly set
      setTimeout(() => {
        if (mounted) {
          setLoading(false)
        }
      }, 100)
    })

    return () => {
      mounted = false;
      unsubscribe();
    }
  }, [])

  const signInWithGoogle = async () => {
    try {
      setLoading(true)
      await AuthService.signInWithGoogle()
    } catch (error) {
      logger.error('Google sign-in failed', undefined, error instanceof Error ? error : undefined)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      setLoading(true)
      await AuthService.signOut()
    } catch (error) {
      logger.error('Sign out failed', undefined, error instanceof Error ? error : undefined)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Register session expired callback so API client can trigger sign-out on 401
  useEffect(() => {
    onSessionExpired(() => {
      signOut().catch(() => {
        // Sign-out failed but session is already expired — user will be redirected on next auth check
      })
    })
    // UU PDP: 403 ACCOUNT_SUSPENDED → force signOut + redirect to login dengan banner
    onAccountSuspended(() => {
      signOut()
        .catch(() => { /* ignore */ })
        .finally(() => {
          try {
            window.localStorage.clear()
          } catch { /* private mode */ }
          window.location.href = '/login?account_suspended=1'
        })
    })
    // Pendaftaran ditutup (27 Sep 2026): akun Google baru tidak dibuatkan akun di backend.
    onRegistrationClosed(() => {
      signOut()
        .catch(() => { /* ignore */ })
        .finally(() => {
          window.location.href = '/register?closed=1'
        })
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getUserInfo = () => {
    return user ? AuthService.getUserInfo(user) : null
  }

  const refreshUserProfile = async () => {
    if (!user) return null
    
    try {
      const profile = await UserService.getOrCreateUser(user.uid, {
        ...(user.email && { email: user.email }),
        ...(user.displayName && { displayName: user.displayName }),
        ...(user.photoURL && { photoURL: user.photoURL })
      })
      setUserProfile(profile)
      return profile
    } catch (error) {
      logger.error('Failed to refresh user profile', undefined, error instanceof Error ? error : undefined)
      return null
    }
  }

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    signInWithGoogle,
    signOut,
    getUserInfo,
    refreshUserProfile
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Helper hook for checking auth status
// eslint-disable-next-line react-refresh/only-export-components
export function useAuthStatus() {
  const { user, loading } = useAuth()
  
  return {
    isAuthenticated: !!user,
    isLoading: loading,
    user
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export default useAuth